import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { App } from './App.tsx';
import { CompareRoute } from './routes/compareRoute.tsx';
import { FixturesRoute } from './routes/fixturesRoute.tsx';
import { LeaguesRoute } from './routes/leaguesRoute.tsx';
import { LedgerRoute } from './routes/ledgerRoute.tsx';
import { RecordPublicRoute } from './routes/recordPublicRoute.tsx';
import { SquadRoute } from './routes/squadRoute.tsx';
import { ThisWeekRoute } from './routes/thisWeekRoute.tsx';
import { TrackerRoute } from './routes/trackerRoute.tsx';
import { TryRoute } from './routes/tryRoute.tsx';
import { UnsubscribeRoute } from './routes/unsubscribeRoute.tsx';

/** The tree's real root renders nothing of its own beyond `<Outlet />`, so
 * `/unsubscribe` below can sit beside `appLayoutRoute` instead of inside it
 * — see that route's own comment. */
const rootRoute = createRootRoute();

/** `App` decides between the signed-out landing/login/register flow and the
 * signed-in shell, and renders `<Outlet />` for whichever of the routes
 * below is active. A pathless layout route (an `id`, not a `path`) rather
 * than the tree's own root (T-069) — that's what lets `/unsubscribe` bypass
 * this gate entirely and stay reachable with no session at all, unlike
 * every route nested under it here. */
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  component: App,
});

/** The This Week desk (T-075) — the app's default surface, still behind auth
 * like `/tracker`. Answers "what should I do right now?" off the gameweek
 * clock, rather than leaving a manager to pick between three unrelated
 * tools on the squad desk below. */
const thisWeekRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: ThisWeekRoute,
});

/** The squad desk — the one-click routine's fine-grained sibling (T-075):
 * still behind auth like `/tracker`, but no longer the default surface. */
const squadRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/squad',
  component: SquadRoute,
});

/** Public since T-049 — the only route under `appLayoutRoute` a signed-out
 * visitor can reach without a redirect to the landing page. `App.tsx` is
 * what makes that true; this route definition itself is no different from
 * the other two. */
const fixturesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/fixtures',
  component: FixturesRoute,
});

/** The free, signed-out squad-and-verdict route (T-088) — the entry id
 * lives in the path itself, `$entryId`, so a result is linkable and
 * survives a reload. Public the same way `/fixtures` above is: this route
 * definition is no different from any other, `App.tsx` is what decides
 * whether a signed-out visitor sees the public shell or the landing page. */
const tryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/try/$entryId',
  component: TryRoute,
});

const trackerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tracker',
  component: TrackerRoute,
});

/** The player-comparison screen (T-051) — behind auth like `/tracker`: no
 * squad is needed to compare players, but a signed-out visitor still gets
 * the landing page here, same as those two routes. */
const compareRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/compare',
  component: CompareRoute,
});

/** The mini-league standings screen (T-054) — behind auth like `/tracker`
 * and `/compare`. */
const leaguesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/leagues',
  component: LeaguesRoute,
});

/** The advice record screen (T-058) — behind auth like `/leagues`,
 * `/compare` and `/tracker`. */
const ledgerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/record',
  component: LedgerRoute,
});

/** The public honesty ledger (T-094) — public the same way `/fixtures` and
 * `/try` above are: this route definition is no different from any other,
 * `App.tsx` is what decides whether a signed-out visitor sees the public
 * shell or the landing page. Named `/record-public` rather than nested under
 * `/record` so it can never be confused with (or accidentally widen the
 * auth gate on) the signed-in `/record` above, which stays private. */
const recordPublicRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/record-public',
  component: RecordPublicRoute,
});

/** The landing spot for every briefing email's unsubscribe link (T-069) — a
 * sibling of `appLayoutRoute`, not a child of it, so it never passes through
 * `App`'s auth gate and stays reachable signed out. See `UnsubscribeRoute`. */
const unsubscribeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/unsubscribe',
  component: UnsubscribeRoute,
});

const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    thisWeekRoute,
    squadRoute,
    fixturesRoute,
    tryRoute,
    trackerRoute,
    compareRoute,
    leaguesRoute,
    ledgerRoute,
    recordPublicRoute,
  ]),
  unsubscribeRoute,
]);

/** A factory rather than a bound module-level singleton — `main.tsx` calls
 * this once for the real app. Tests call it per-render so each gets its own
 * router instance (and its own subscription to the real browser history)
 * instead of leaking matched-route state between tests. */
export function createAppRouter() {
  return createRouter({ routeTree });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
